import {
  type DocumentData,
  type FirestoreDataConverter,
  type PartialWithFieldValue,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type WithFieldValue,
  collection,
  doc,
  type Firestore,
} from 'firebase/firestore';

import type {
  Transaction,
  Course,
  Enrollment,
  Tasa,
  LegalVersion,
  UserLegalAcceptance,
  AdminSettings,
  AdminPrivate,
  BillingRecord,
  MarketingConfig,
  MarketingThread,
  PaymentMethod,
  LandingMetrics,
  GlobalAdmin,
  MarketingAd,
  MarketingAdSet,
  MarketingCampaign,
  GlobalPixel,
  Public,
  Slug,
  Business,
  Client,
  Sale,
  Balance,
  Product,
  OwnerType,
} from '../schema';

import { FirestorePaths } from './firestore-paths';

/**
 * TIPO PARA EL DOCUMENTO DE MÉTODOS DE PAGO GLOBALES
 */
export interface GlobalPaymentMethodsDoc {
  methods: PaymentMethod[];
  updatedAt: any;
}

/**
 * CONVERTER GENÉRICO SEGURO
 * T: El tipo de los datos en Firestore (sin ID).
 * Retorna T & { id: string } para el frontend.
 */
export const createConverter = <T extends object>(): FirestoreDataConverter<
  T & { id: string },
  T
> => ({
  toFirestore: (data: WithFieldValue<T & { id: string }>): WithFieldValue<T> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...rest } = data as any;
    return rest;
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): T & { id: string } => {
    const data = snapshot.data(options) as T;
    return {
      ...data,
      id: snapshot.id,
    };
  },
});

/**
 * 🏭 FIRESTORE REFS FACTORY (Unified CRM Pivot)
 * Esta función centraliza todas las referencias a Firestore de la aplicación.
 * Recibe la instancia de la DB para ser usada tanto en App como en Public.
 */
export const createFirestoreRefs = (db: Firestore) => {
  return {
    // Marketing Config
    marketingConfigRef: (ownerId: string) =>
      doc(db, FirestorePaths.marketingConfig(ownerId)).withConverter(
        createConverter<MarketingConfig>(),
      ),

    // Helpers & Globals
    tasaDoc: () => doc(db, FirestorePaths.tasaDoc).withConverter(createConverter<Tasa>()),
    publicRef: (ownerId: string) => doc(db, FirestorePaths.publicDoc(ownerId)).withConverter(createConverter<Public>()),

    // Slugs
    slugRef: (slug: string) =>
      doc(db, FirestorePaths.slugDoc(slug)).withConverter(createConverter<Slug>()),
    slugsCol: () => collection(db, FirestorePaths.slugs).withConverter(createConverter<Slug>()),

    // Owners (Business Only in v2)
    ownerRef: (ownerId: string) =>
      doc(db, FirestorePaths.ownerDoc(ownerId)).withConverter(
        createConverter<Business>(),
      ),
    ownersCol: () =>
      collection(db, FirestorePaths.owners).withConverter(
        createConverter<Business>(),
      ),

    // Sales
    saleRef: (ownerId: string, id: string) =>
      doc(db, FirestorePaths.saleDoc(ownerId, id)).withConverter(createConverter<Sale>()),
    salesCol: (ownerId: string) =>
      collection(db, FirestorePaths.sales(ownerId)).withConverter(createConverter<Sale>()),

    // Clients / Leads (Unified)
    clientsCol: (ownerId: string) =>
      collection(db, FirestorePaths.clients(ownerId)).withConverter(
        createConverter<Client>(),
      ),
    clientDoc: (ownerId: string, clientId: string) =>
      doc(db, FirestorePaths.clientDoc(ownerId, clientId)).withConverter(
        createConverter<Client>(),
      ),

    // Products / Services
    productsCol: (ownerId: string) =>
      collection(db, FirestorePaths.products(ownerId)).withConverter(
        createConverter<Product>(),
      ),
    productRef: (ownerId: string, productId: string) =>
      doc(db, FirestorePaths.productDoc(ownerId, productId)).withConverter(
        createConverter<Product>(),
      ),
    productDoc: (ownerId: string, productId: string) =>
      doc(db, FirestorePaths.productDoc(ownerId, productId)).withConverter(
        createConverter<Product>(),
      ),

    // Transactions
    transactionsCol: (ownerId: string) =>
      collection(db, FirestorePaths.transactions(ownerId)).withConverter(
        createConverter<Transaction>(),
      ),
    transactionRef: (ownerId: string, transactionId: string) =>
      doc(db, FirestorePaths.transactionDoc(ownerId, transactionId)).withConverter(
        createConverter<Transaction>(),
      ),

    // Balances
    balancesCol: (ownerId: string) =>
      collection(db, FirestorePaths.balances(ownerId)).withConverter(
        createConverter<Balance>(),
      ),
    balanceDoc: (ownerId: string, workerId: string = 'main') =>
      doc(db, FirestorePaths.balanceDoc(ownerId, workerId)).withConverter(
        createConverter<Balance>(),
      ),
    settlementsCol: (ownerId: string) =>
      collection(db, FirestorePaths.settlements(ownerId)).withConverter(
        createConverter<DocumentData>(),
      ),
    settlementRef: (ownerId: string, settlementId: string) =>
      doc(db, FirestorePaths.settlementDoc(ownerId, settlementId)).withConverter(
        createConverter<DocumentData>(),
      ),

    // Academy
    coursesCol: (ownerId: string) =>
      collection(db, FirestorePaths.courses(ownerId)).withConverter(
        createConverter<Course>(),
      ),
    courseDoc: (ownerId: string, courseId: string) =>
      doc(db, FirestorePaths.courseDoc(ownerId, courseId)).withConverter(
        createConverter<Course>(),
      ),
    courseRef: (ownerId: string, courseId: string) =>
      doc(db, FirestorePaths.courseDoc(ownerId, courseId)).withConverter(
        createConverter<Course>(),
      ),
    enrollmentsCol: (ownerId: string) =>
      collection(db, FirestorePaths.enrollments(ownerId)).withConverter(
        createConverter<Enrollment>(),
      ),
    enrollmentRef: (ownerId: string, enrollmentId: string) =>
      doc(db, FirestorePaths.enrollmentDoc(ownerId, enrollmentId)).withConverter(
        createConverter<Enrollment>(),
      ),
    // Public Profiles (Mirror for SEO/Fast Read)
    publicProfilesCol: () =>
      collection(db, FirestorePaths.public).withConverter(
        createConverter<Public>(),
      ),
    publicProfileDoc: (ownerId: string) =>
      doc(db, FirestorePaths.publicDoc(ownerId)).withConverter(
        createConverter<Public>(),
      ),

    // Admin Specific
    adminSettingsRef: (ownerId: string) =>
      doc(db, FirestorePaths.adminSettings(ownerId)).withConverter(
        createConverter<AdminSettings>(),
      ),
    adminPrivateRef: (ownerId: string) =>
      doc(db, FirestorePaths.adminPrivate(ownerId)).withConverter(
        createConverter<AdminPrivate>(),
      ),
    adminBillingCol: (ownerId: string) =>
      collection(db, FirestorePaths.adminBilling(ownerId)).withConverter(
        createConverter<BillingRecord>(),
      ),

    // Global Payment Methods
    globalPaymentMethodsDoc: () =>
      doc(db, FirestorePaths.globalPaymentMethods).withConverter(
        createConverter<GlobalPaymentMethodsDoc>(),
      ),

    // Legal Documents
    legalVersionsCol: () =>
      collection(db, FirestorePaths.legalVersions).withConverter(
        createConverter<LegalVersion>(),
      ),
    userLegalAcceptancesCol: () =>
      collection(db, FirestorePaths.legalAcceptances).withConverter(
        createConverter<UserLegalAcceptance>(),
      ),

    // Expenses
    expensesCol: (ownerId: string) =>
      collection(db, FirestorePaths.expenses(ownerId)).withConverter(
        createConverter<DocumentData>(),
      ),
    expenseRef: (ownerId: string, expenseId: string) =>
      doc(db, FirestorePaths.expenseDoc(ownerId, expenseId)).withConverter(
        createConverter<DocumentData>(),
      ),

    // Marketing (Leads, Campaigns, Pixels)
    marketingThreadsCol: (ownerId: string) =>
      collection(db, FirestorePaths.marketingThreads(ownerId)).withConverter(
        createConverter<MarketingThread>(),
      ),
    marketingThreadDoc: (ownerId: string, threadId: string) =>
      doc(db, FirestorePaths.marketingThreadDoc(ownerId, threadId)).withConverter(
        createConverter<MarketingThread>(),
      ),
    marketingCampaignsCol: (ownerId: string) =>
      collection(db, FirestorePaths.marketingCampaigns(ownerId)).withConverter(
        createConverter<MarketingCampaign>(),
      ),
    marketingCampaignDoc: (ownerId: string, campaignId: string) =>
      doc(db, FirestorePaths.marketingCampaignDoc(ownerId, campaignId)).withConverter(
        createConverter<MarketingCampaign>(),
      ),
    marketingCampaignRef: (ownerId: string, campaignId: string) =>
      doc(db, FirestorePaths.marketingCampaignDoc(ownerId, campaignId)).withConverter(
        createConverter<MarketingCampaign>(),
      ),
    marketingAdsCol: (ownerId: string) =>
      collection(db, FirestorePaths.marketingAds(ownerId)).withConverter(
        createConverter<MarketingAd>(),
      ),
    marketingAdRef: (ownerId: string, adId: string) =>
      doc(db, FirestorePaths.marketingAdDoc(ownerId, adId)).withConverter(
        createConverter<MarketingAd>(),
      ),
    marketingAdSetsCol: (ownerId: string) =>
      collection(db, FirestorePaths.marketingAdSets(ownerId)).withConverter(
        createConverter<MarketingAdSet>(),
      ),
    marketingAdSetRef: (ownerId: string, adsetId: string) =>
      doc(db, FirestorePaths.marketingAdSetDoc(ownerId, adsetId)).withConverter(
        createConverter<MarketingAdSet>(),
      ),
    globalPixelsCol: () =>
      collection(db, FirestorePaths.globalPixels).withConverter(
        createConverter<GlobalPixel>(),
      ),
    globalPixelRef: (pixelId: string) =>
      doc(db, FirestorePaths.globalPixelDoc(pixelId)).withConverter(
        createConverter<GlobalPixel>(),
      ),
    landingMetricsCol: () =>
      collection(db, FirestorePaths.landingMetrics).withConverter(
        createConverter<LandingMetrics>(),
      ),
    landingMetricsDoc: () =>
      doc(db, FirestorePaths.landingMetrics).withConverter(
        createConverter<LandingMetrics>(),
      ),
    globalAdminDoc: () =>
      doc(db, FirestorePaths.globalAdmin).withConverter(
        createConverter<GlobalAdmin>(),
      ),
  };
};

export type FirestoreRefs = ReturnType<typeof createFirestoreRefs>;
